import { Button, Checkbox, FormControlLabel, Grid, IconButton, InputBase, Menu, Paper, TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';
import React from 'react';
import { RootState } from "../../common/Store";
import { useAppSelector } from '../../common/hooks';
import { useConstructor } from '../../common/UseConstructor';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import { globalTheme } from '../../common/theme';
import { FilterAlt } from '@mui/icons-material';

//Base control for all filters
export interface BaseControl {
    key: string,
    title: string,
}

//Checkbox - True/False
export interface CheckboxControl extends BaseControl {
    checked: boolean
}

//Individual option for a dropdown combo
export interface DropDownComboOption {
    name: string,
    value: string,
    tag?: any,
}

//Multi select filter
export interface DropDownComboControl extends BaseControl {
    dropdownOptions: DropDownComboOption[],
}

//Individual option for a mulit-select
export interface MultiSelectOption {
    name: string,
    value: string,
    tag?: any,
}

//TextField 
export interface InputBox extends BaseControl {
    value: string
}

//Multi select filter
export interface MultiSelectControl extends BaseControl {
    options: MultiSelectOption[],
    filter?: (items: MultiSelectOption[], searchData: any) => MultiSelectOption[],
    groupBy?: (option: MultiSelectOption) => string
}

export interface DatePickerControl extends BaseControl {
    dateValue: Date //The value of dateValue does not matter, it is purely used to identify it is a DatePickerControl
}

//We use this to check if an item is a specific BaseControl since we don't have the same functionality as C#
function Is<T>(object: any, field: string): object is T {
    return object.hasOwnProperty(field);
}

//Generic filter that will auto-populate based on supplied controls. We use this to keep the filters all looking/feeling the same
export function GenericFilter(props: { controls: BaseControl[], searchData: any, setSearchData: Function, doSearch: Function, searchBoxLabel: string }) {
    const style = { ...globalTheme() };

    const searchBoxRef = React.createRef<HTMLInputElement>()

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    //For anchoring the menu dropdown (the html element its attached to)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    //Set the html element to show the menu on
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(searchBoxRef.current);
    };

    //Clear the html element, so the menu closes
    const handleClose = () => {
        setAnchorEl(null);
        props.doSearch(null);
    };

    //Left this here for now incase we need a constructor
    useConstructor(() => {
    })

    //For checkbox
    const updateFilterChecked = (event: React.ChangeEvent<HTMLInputElement>, control: BaseControl) => {
        var newValue = { ...props.searchData }
        newValue[control.key] = event.currentTarget.checked;
        props.setSearchData(newValue);
    }

    //For dropdown combo
    const updateFilterSelect = (item: any, control: BaseControl) => {
        var newValue = { ...props.searchData }
        if (item == null)
            newValue[control.key] = null;
        else
            newValue[control.key] = item.value;
        props.setSearchData(newValue);
    }

    //For multiselect
    const updateFilterMultiSelect = (items: any[], control: BaseControl) => {
        var newValue = { ...props.searchData }
        if (items == null)
            newValue[control.key] = [];
        else
            newValue[control.key] = items.map(i => i.value);
        props.setSearchData(newValue);
    }

    //For date picker
    const updateFilterDate = (item: any, control: BaseControl) => {
        var newValue = { ...props.searchData }
        newValue[control.key] = item;
        props.setSearchData(newValue);
    }

    //For text field
    const updateFilterText = (value: any, control: BaseControl) => {
        var newValue = { ...props.searchData }
        newValue[control.key] = value;
        props.setSearchData(newValue);
    }

    const updateSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
        var newValue = { ...props.searchData }
        newValue['searchText'] = e.target.value;
        props.setSearchData(newValue);
    }

    const submitClicked = (e: React.FormEvent<any>) => {
        props.doSearch(e);
    }

    return (
        <div>
            <div className={style.PageFilter}>
                <Paper variant="outlined" className={style.searchBar} component="form" onSubmit={submitClicked} >
                    <div style={{ display: 'flex'}}>
                        <div style={{width: '100%'}}>
                            <Paper variant="outlined" style={{ display: 'flex' }} >
                                <InputBase
                                    name="SearchBox"
                                    style={{ flex: '1', paddingLeft: "5px" }}
                                    placeholder={props.searchBoxLabel}
                                    inputProps={{ 'aria-label': 'Search' }}
                                    onChange={updateSearchText}
                                    ref={searchBoxRef}
                                />
                                {props.controls.length>0 && 
                                    <IconButton aria-label="search" size="large" onClick={handleClick}>
                                        <FilterAlt />
                                    </IconButton>
                                }
                                {props.controls.length==0 && 
                                    <div style={{ height: "40px"}}></div>
                                }
                            </Paper>
                        </div>
						<div style={{ width: '100px', paddingTop: "5px", paddingLeft: "8px" }}>
                            <Button type="submit" aria-controls="filter-menu" aria-haspopup="true" variant="contained" color="secondary" fullWidth>
                                Search
                            </Button>
						</div>
					</div>
                    <Menu
                        id="filter-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        PaperProps={{
                            style: {
                                width: "30vw",
                                minWidth: "500px",
                                maxHeight: "50vh",
                                padding: '20px'
                            }
                        }}
                    >
                        <Grid container spacing={2}>
                            {props.controls.map((item, index) => {
                                if (Is<CheckboxControl>(item, "checked")) {
                                    return (
                                        <Grid item xs={12} key={item.key} >
                                            <FormControlLabel
                                                key={item.key}
                                                style={{ display: 'block' }}
                                                control={
                                                    <Checkbox checked={props.searchData[item.key]} onChange={(e) => updateFilterChecked(e, item)} />
                                                }
                                                label={item.title}
                                            />
                                        </Grid>
                                    )
                                } else if (Is<InputBox>(item, "value")) {
                                    return (
                                        <Grid item xs={12} key={item.key} >
                                            <TextField 
                                                style={{ width: '100%' }}
                                                label={item.title}
                                                value={item.value}
                                                onChange={(e) => updateFilterText(e.target.value, item)}
                                            />
                                        </Grid>
                                    )
                                } else if (Is<DropDownComboControl>(item, "dropdownOptions")) {
                                    return (
                                        <Grid item xs={12} key={item.key} >
                                            <Autocomplete
                                                key={item.key}
                                                fullWidth={true}
                                                limitTags={2}
                                                size="small"
                                                id={item.key}
                                                onChange={(e, selected) => updateFilterSelect(selected, item)}
                                                options={item.dropdownOptions}
                                                getOptionLabel={(option) => option.name}
                                                renderOption={(props, option) => {
                                                    return (
                                                        <li {...props} key={option.value}>
                                                        {option.name}
                                                        </li>
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                        label={item.title}
                                                        onKeyDownCapture={e =>
                                                            e.key === 'c' || e.key === 'C'
                                                              ? e.stopPropagation()
                                                              : undefined
                                                          }
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )
                                } else if (Is<MultiSelectControl>(item, "options")) {
                                    return (
                                        <Grid item xs={12} key={item.key} >
                                            <Autocomplete
                                                key={item.key}
                                                fullWidth={true}
                                                limitTags={2}
                                                size="small"
                                                id={item.key + '-multi-select'}
                                                disableCloseOnSelect
                                                disableListWrap
                                                multiple
                                                onChange={(e, selected) => updateFilterMultiSelect(selected, item)}
                                                options={item.filter ? item.filter(item.options, props.searchData) : item.options}
                                                getOptionLabel={(option) => option.name}
                                                groupBy={item.groupBy}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                        label={item.title}
                                                    />
                                                )}
                                                renderOption={(p, option, state) => (
                                                    <React.Fragment>
                                                        <Checkbox checked={props.searchData[item.key]?.indexOf(option.value) > -1} />

                                                        {option.name}
                                                    </React.Fragment>
                                                )}
                                            />
                                        </Grid>
                                    )
                                } else if (Is<DatePickerControl>(item, "dateValue")) {
                                    return (
                                        <Grid item xs={6} key={item.key} >
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    key={item.key}
                                                    label={item.title}
                                                    inputFormat="yyyy/MM/dd"
                                                    mask="____/__/__"
                                                    value={props.searchData[item.key]}
                                                    onChange={(newValue) => {
                                                        updateFilterDate(newValue, item);
                                                    }}
                                                    renderInput={(params) => <TextField {...params} style={{width: '100%'}}/>}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    )
                                } else {
                                    if (isDebug) console.log("unmatched: " + item);
                                    return (<div key={item.key}></div>)
                                }
                            })}
                        </Grid>
                    </Menu>
                </Paper>
            </div>
        </div>

    )
}